// src/pages/landing-page/utils/MatomoTracker.tsx
import {useEffect, useState} from "react";
import {configApi} from "@/boxhub-api";
import type {MatomoConfigDTO} from "@/api/data-contracts";
import {useAuth} from "@/app/auth/AuthContext";

/**
 * Initializes Matomo tracking on the Landing Page.
 * - Runs only if environment === "prod"
 * - Loads config from backend (url, siteId)
 * - Avoids duplicate script injection
 * - Uses correct Matomo call order
 */
export const useMatomoTracker = () => {
    const {environment} = useAuth();
    const [matomoConfig, setMatomoConfig] = useState<MatomoConfigDTO | undefined>(undefined);

    const isProd = environment?.toLowerCase() === "prod";

    useEffect(() => {
        if (!isProd) {
            return;
        }
        let cancelled = false;

        configApi
                .getMatomoConfig()
                .then((resp) => {
                    if (!cancelled) {
                        setMatomoConfig(resp.data);
                    }
                })
                .catch((err) => {

                    console.error("Error fetching Matomo config:", err);
                });

        return () => {
            cancelled = true;
        };
    }, [isProd]);

    useEffect(() => {
        if (!isProd) {
            return;
        }
        if (!matomoConfig?.url || !matomoConfig?.siteId) {
            return;
        }
        if (typeof window === "undefined" || typeof document === "undefined") {
            return;
        }

        // Prevent duplicate injection
        if (document.getElementById("matomo-js")) {
            return;
        }

        const w = window as any;
        const _paq = (w._paq = w._paq || []);

        // Correct order: config before tracking
        _paq.push(["setDoNotTrack", true]);
        _paq.push(["setTrackerUrl", `${matomoConfig.url.replace(/\/?$/, "/")}matomo.php`]);
        _paq.push(["setSiteId", `${matomoConfig.siteId}`]);
        _paq.push(["enableLinkTracking"]);
        _paq.push(["trackPageView"]);

        const g = document.createElement("script");
        g.async = true;
        g.id = "matomo-js";
        g.src = `${matomoConfig.url.replace(/\/?$/, "/")}matomo.js`;

        const firstScript = document.getElementsByTagName("script")[0];
        if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(g, firstScript);
        } else {
            document.head.appendChild(g);
        }

        return () => {
            g.remove();
        };
    }, [isProd, matomoConfig]);
};
